/**
 * Central configuration. Every tunable physics / gameplay number lives here so the
 * feel of the game can be iterated on without touching logic code.
 *
 * Units: pixels and seconds. The world is a tile grid of TILE pixels.
 */

export const TILE = 16;

/** Internal render resolution (integer-scaled to the window). NES-ish 16x14 tiles. */
export const VIEW_W = 256;
export const VIEW_H = 224;

/** Fixed logic step. 1/120s keeps the physics identical on 60/90/120/144Hz screens. */
export const FIXED_DT = 1 / 120;
/** Never simulate more than this many seconds worth of steps in a single frame. */
export const MAX_FRAME_TIME = 0.25;

export const PHYSICS = {
  /** Gravity while falling, or while rising without the jump button held. */
  gravity: 1900,
  /** Reduced gravity while the jump button is held and the player is still rising. */
  gravityJumpHold: 820,
  /** Terminal fall speed. */
  maxFall: 268,

  /** Base jump impulse. */
  jumpVelocity: 332,
  /** Extra impulse scaled by |vx| (running jumps go a little higher). */
  jumpSpeedBonus: 0.14,
  /** On jump release while rising, upward speed is multiplied by this. */
  jumpCutFactor: 0.38,
  /** Grace period after walking off a ledge during which a jump still works. */
  coyoteTime: 0.075,
  /** Jump presses are remembered for this long before touching the ground. */
  jumpBuffer: 0.1,

  /** Horizontal speed caps. */
  walkMax: 92,
  runMax: 152,
  /** Acceleration while walking / running on the ground. */
  groundAccel: 560,
  runAccel: 720,
  /** Deceleration when no direction is held on the ground. */
  groundFriction: 700,
  /** Deceleration while holding the direction opposite to travel (skid). */
  skidDecel: 1500,
  /** Air control is weaker than ground control. */
  airAccel: 340,
  /** Slight air drag when no direction is held. */
  airDrag: 70,
  /** Speed above which a reversing player is considered skidding (dust + anim). */
  skidThreshold: 46,

  /** Upward impulse after stomping an enemy (bigger when jump is held). */
  stompBounce: 232,
  stompBounceHeld: 318,

  /** Maximum pixels resolved per collision sub-step (prevents tunnelling). */
  maxStep: 6,
} as const;

export const PLAYER = {
  smallW: 11,
  smallH: 14,
  bigW: 12,
  bigH: 23,
  /** Freeze frames while growing / shrinking / gaining fire power. */
  transformTime: 0.5,
  /** Blinking invulnerability after taking a hit. */
  hurtInvuln: 2.0,
  /** Duration of the prism (star) power. */
  prismTime: 9.0,
  /** Fire cooldown and maximum simultaneous orbs. */
  orbCooldown: 0.24,
  maxOrbs: 2,
  /** Death animation timings. */
  deathHang: 0.5,
  deathPop: 300,
  respawnDelay: 2.0,
  startLives: 3,
} as const;

export const ENEMY = {
  grubSpeed: 30,
  grubSquashTime: 0.45,
  snugSpeed: 34,
  /** Shell revives after this long if left alone (0 disables). */
  shellReviveTime: 8.0,
  shellKickSpeed: 210,
  /** Nipper (pipe enemy) cycle. */
  nipperHidden: 1.9,
  nipperRise: 0.45,
  nipperOut: 1.6,
  /** Nipper will not emerge while the player is this close horizontally. */
  nipperSafeRange: 34,
  /** Enemies wake up when within this many pixels of the camera view. */
  activateMargin: 24,
  /** Enemies are removed once this far behind the camera. */
  despawnMargin: 96,
} as const;

export const ITEM = {
  berrySpeed: 62,
  prismSpeed: 74,
  prismBounce: 210,
  /** Time an item takes to rise out of a block. */
  emergeTime: 0.55,
  orbSpeed: 236,
  orbBounce: 196,
  orbLife: 3.4,
  coinPopVy: 260,
} as const;

export const SCORE = {
  coin: 200,
  brick: 50,
  stompChain: [100, 200, 400, 800, 1000, 2000, 4000, 8000] as const,
  shellChain: [500, 800, 1000, 2000, 4000, 8000] as const,
  berry: 1000,
  ember: 1000,
  prism: 1000,
  life: 0,
  flagBonus: 2000,
  timeBonus: 50,
  coinsPerLife: 100,
} as const;

export const CAMERA = {
  /** Player is kept at this fraction of the screen width while advancing. */
  anchorX: 0.44,
  /** Horizontal follow smoothing (higher = snappier). */
  lerpX: 12,
  lerpY: 6,
  /** Vertical dead zone: camera only follows once the player leaves this band. */
  deadZoneTop: 0.3,
  deadZoneBottom: 0.72,
  shakeDecay: 7,
  maxShake: 5,
} as const;

export const GAME = {
  /** Countdown ticks per real second (classic games run the clock fast). */
  timerRate: 2.5,
  /** Warning jingle threshold. */
  timeWarn: 100,
  debugDefault: false,
} as const;

/** Defaults for the persisted option set. */
export const DEFAULT_OPTIONS = {
  musicOn: true,
  sfxOn: true,
  musicVol: 55,
  sfxVol: 80,
  checkpoints: true,
  backtrack: false,
  touchMode: 'auto' as 'auto' | 'on' | 'off',
  scaleMode: 'fit' as 'fit' | '2' | '3' | '4',
};

export type Options = typeof DEFAULT_OPTIONS;
